import type { AlertEventResponse } from '@/utils/apiClient';

export const CONDITION_SYMBOLS: Record<string, string> = {
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
  eq: '==',
  neq: '!=',
};

export interface EventMessageTemplates {
  eventFiringMsg: string;
  eventResolvedMsg: string;
}

// The event carries the rule's CURRENT condition and threshold (joined at read
// time), not the ones it fired against. They describe this event only if the
// rule has not been changed since the event fired; otherwise the stored
// message is the record. Any missing or unparseable timestamp counts as
// "cannot tell" and also falls back to the stored message.
// The two early returns are equivalent mutants: Date.parse of a missing or
// junk value is NaN and any comparison with NaN is false. They stay so the
// intent reads directly; the fallback test pins the behaviour either way.
function ruleUnchangedSinceFired(ev: AlertEventResponse): boolean {
  if (!ev.fired_at || !ev.rule_updated_at) return false;
  const fired = Date.parse(ev.fired_at);
  const updated = Date.parse(ev.rule_updated_at);
  if (Number.isNaN(fired) || Number.isNaN(updated)) return false;
  return fired > updated;
}

// STORY-380: a fleet-wide rule raises one event per breaching entity, and the
// backend sets entity_name on exactly those events (the display name, or the
// id itself when nobody can name it). Selector rules and text rules leave it
// null, so their line reads exactly as before.
export function entityLabel(ev: AlertEventResponse): string {
  const { entity_id: id, entity_name: name } = ev;
  if (!name || id === '__global__') return '';
  return id && id !== name ? `${name} (${id})` : name;
}

// The stored, Telegram and digest texts spell numbers the Python way: a whole
// float is "3.0". JSON drops the ".0", so write it back (BUG-1.6.0_15).
// Agrees with Python's str() from 1e-4 up to 1e16; outside that range the two
// pick exponent notation at different points.
export function storedNumber(n: number): string {
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
}

export function formatEventMessage(
  ev: AlertEventResponse,
  templates: EventMessageTemplates,
): string {
  const stored = ev.message || '';
  const { current_value: value, condition, threshold } = ev;
  if (value == null || condition == null || threshold == null) return stored;
  if (!ruleUnchangedSinceFired(ev)) return stored;
  const template =
    ev.status === 'resolved' ? templates.eventResolvedMsg : templates.eventFiringMsg;
  const label = entityLabel(ev);
  const vars: Record<string, string> = {
    name: label ? `${ev.rule_name || ''} — ${label}` : ev.rule_name || '',
    value: storedNumber(value),
    condition: CONDITION_SYMBOLS[condition] || condition,
    threshold: storedNumber(threshold),
  };
  // One pass, function replacer: a rule name containing "$&" or "{value}"
  // is inserted verbatim.
  return template.replace(/\{(name|value|condition|threshold)\}/g, (_m, k: string) => vars[k]);
}
